import React, {useEffect, useState, useRef, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams, useNavigate} from 'react-router-dom';
import PageBase from "../../../components/PageBase/index";
import Field from "../../../components/FieldGroup/Field/index";
import * as campaignActions from "../../../redux/Campaign/actions";
import * as ROUTES from "../../../utils/routes";
import FieldGroup from "../../../components/FieldGroup/index";
import ObjectSummaryRow from "../../../components/ObjectSummaryRow";
import Button from "../../../components/Button";
import Toaster from "../../../components/Toaster";
import {showConfirm} from "../../../components/ConfirmBox";
import PopupWin from "../../../components/PopupWin";
import PopupButton from "../../../components/PopupButton";
import EditCampaign from "../EditCampaign";
import moment from "moment";
import LoadingPanel from "../../../components/LoadingPanel";
import {camelCaseToProperEnglish} from "../../../utils/general";
import { formatUTCdateWithTime } from '../../../utils/general';
import './ViewCampaign.scss';

const ViewCampaign = props => {
	const dispatch = useDispatch();
	const params = useParams();
	const navigate = useNavigate();
	const campaign = useSelector(state => state.campaign.campaigns?.find(c => c.campaign_id.toString() === params.id.toString()));
	const [editCampaign, setEditCampaign] = useState(null);
	const [loading, setLoading] = useState(true);
	const editCampaignRef = useRef(null);

	useEffect(() => {
		const load = async () => {
			await dispatch(campaignActions.loadCampaignById(params.id, true))
			setLoading(false);
		};
		load();
	}, [params.id]);

	const editCampaignFooter = useMemo(() => (
		<PopupButton
			variant="primary"
			onClick={() => editCampaignRef.current?.save()}
		>
			{editCampaign?.campaign_id ? 'Save' : 'Create'}
		</PopupButton>
	), [editCampaign?.campaign_id]);

	if(campaign == null) {
		return <div/>;
	}

	const summary = [
		{ title: 'Account Name', value: campaign.account_name, blue: true},
		{ title: 'campaign Name', value: campaign.campaign_name},
		{ title: 'Region', value: campaign.region_name},
		{ title: 'Subsidiary', value: campaign.subsidiary_name},
		{ title: 'Type', value: campaign.campaign_type_name},
		{ title: 'Status', value: campaign.campaign_status_name}
	];

	const activateCampaign = () => {
		setLoading(true);
		dispatch(campaignActions.activateCampaign(campaign.campaign_id, campaign.vendor, campaign.reply_campaign_id,campaign.is_manual, campaignActivated));
	};

	const campaignActivated = (result) => {
		setLoading(false);
		if(result.status === "ok") {
			Toaster.showSuccess("Campaign successfully activated");
		}
		else if(result.message === "campaign_without_contacts") {
			Toaster.showError("Campaign must have at least 1 contact");
		}
		else if(result.message === "missing_reply_campaign") {
			Toaster.showError("Campaign could not be activated. Check Reply.IO campaign Id.");
		}
	};

	const resumeCampaign = () => {
		setLoading(true);
		dispatch(campaignActions.resumeCampaign(campaign.campaign_id, campaign.reply_campaign_id,campaign.is_manual, campaignResumed));
	};

	const campaignResumed = (result) => {
		setLoading(false);
		if(result.status === "ok") {
			Toaster.showSuccess("Campaign successfully resumed.");
		}
	};

	const pauseCampaign = () => {
		setLoading(true);
		dispatch(campaignActions.pauseCampaign(campaign.campaign_id, campaign.reply_campaign_id,campaign.is_manual, campaignPaused));
	};

	const campaignPaused = (result) => {
		setLoading(false);
		if(result.status === "ok") {
			Toaster.showSuccess("Campaign successfully paused.");
		}
	};

	const showConfirmDelete = () => {
		showConfirm({
			title: `Delete Campaign`,
			message: `Campaign ${campaign.campaign_name} will be deleted.<br/><br/>Would you like to continue?`,
			onConfirm: () => onDeleteCampaign(),
		})
	};

	const onDeleteCampaign = () => {
		dispatch(campaignActions.deleteCampaign(campaign.campaign_id, campaignDeleted));
	};

	const campaignDeleted = (result) => {
		if(result.status === "ok") {
			Toaster.showSuccess("Campaign successfully deleted");
			navigate(`/campaigns`);
		}
		else {
			Toaster.showError("Failed to delete campaign");
			console.log(result);
		}
	};

	const onCampaignSaved = async () => {
		setEditCampaign(null);
		navigate(`/campaigns`);
	};

	if(loading) {
		return (
			<PageBase title="Campaign"
			          removeMargins
			          hideRefresh
			          bodyBg="#fff"
			          headerLinkText="All Campaigns"
			          headerLinkTarget={ROUTES.CAMPAIGNS}
			>
				<LoadingPanel />
			</PageBase>
		)
	}

	const renderStepStats = () => {
		return (
			<FieldGroup title="Results" expandable>
				{
					campaign.stepsStats.map((step, i) => {
						return (
							<React.Fragment key={i}>
								<div style={{padding: '10px 0px', fontSize: '14px', fontWeight: 'bold'}}>Step {step.stepNumber}</div>
								<div/>
								{
									Object.keys(step)
										.filter(field => field !== 'campaignId' && field !== 'campaignName' && field !== 'stepId' && field !== 'stepNumber')
										.map((field, j) => {
										return (
											<Field key={j} title={camelCaseToProperEnglish(field)}>{step[field]}</Field>
										)
									})
								}
							</React.Fragment>
						)
					})
				}
				<br/><br/><br/><br/>
			</FieldGroup>
		)
	};

	return (
		<PageBase title="Campaign"
		          removeMargins
		          hideRefresh
		          bodyBg="#fff"
		          headerLinkText="All Campaigns"
		          headerLinkTarget={ROUTES.CAMPAIGNS}
		>

			<PopupWin
				closeOnEscape={false}
				closeOnDocumentClick={false}
				visible={editCampaign != null}
				onClose={() => setEditCampaign(null)}
				title="Campaign"
				titleStyle={{fontSize: '18px', margin: '0 0 25px 0'}}
				contentStyle={{width: '100%', height: '90%', padding: 0}}
				popupStyle={{width: '90%', maxWidth: '1600px', height: '84%', maxHeight: '800px', overflow: 'none'}}
				footerStyle={{borderTop: '1px solid #D3E1ED'}}
				footer={editCampaignFooter}>
				{ editCampaign && <EditCampaign ref={editCampaignRef} editCampaign={editCampaign} onClose={onCampaignSaved} /> }
			</PopupWin>

			<div className="view-campaign-title">
				<div style={{fontSize: '14px'}}>{campaign.campaign_name}</div>
				<div className="view-campaign-buttons">
					{
						// planned
						campaign.campaign_status_id === 1 ?
							<Button onClick={activateCampaign} type="blue" center>Activate</Button>
							// in progress
							: campaign.campaign_status_id === 2 ?
								<Button onClick={pauseCampaign} type="blue" center>Pause</Button>
								// paused
								: campaign.campaign_status_id === 3 ?
									<Button onClick={resumeCampaign} type="blue" center>Resume</Button>
									:
									<div/>
					}
					<Button onClick={() => navigate(`/campaign/${campaign.campaign_id}/members`)} type="blue" center>Members</Button>
					<Button onClick={() => setEditCampaign({})} type="blue" center>New</Button>

					{
						campaign.campaign_status_id === 1 &&
						<Button onClick={() => setEditCampaign(campaign)} type="blue" center>Edit</Button>
					}

					{
						campaign.campaign_status_id === 1 &&
						<Button onClick={showConfirmDelete} type="blue" center>Delete</Button>
					}
				</div>
			</div>

			<ObjectSummaryRow data={summary} />

			<div style={{height: '100%', overflowY: 'auto', padding: '0 20px'}}>
				<FieldGroup title="Details" expandable expanded>
					<Field title="Campaign Owner" highlight>{campaign.campaign_owner_name}</Field>
					<Field title="Campaign Name">{campaign.campaign_name}</Field>

					<Field title="Vendor">{campaign.vendor.toUpperCase()}</Field>

					<Field title="Type">{campaign.campaign_type_name}</Field>
					<Field title="Status">{campaign.campaign_status_name}</Field>
					<Field title="Last Status Update">{formatUTCdateWithTime(campaign.last_status_update)}</Field>

					<Field title="Unique Identifier">{campaign.unique_identifier}</Field>
					<Field title="Parent Campaign">{campaign.parent_campaign_name || ''}</Field>
					<Field title="Is Manuel">{campaign.is_manual?'Yes':'No'}</Field>

					{/* <Field title="Reply.IO Campaign ID">{campaign.reply_campaign_id}</Field> */}
					<Field title="Description">{campaign.description}</Field>
				</FieldGroup>

				<FieldGroup title="Segmentation" expandable>
					<Field title="Region">{campaign.region_name}</Field>
					<Field title="Subsidiary">{campaign.subsidiary_name || ''}</Field>

					<Field title="Industry">{campaign.industry_name || ''}</Field>
					<Field title="Company Segment">{campaign.company_segment_name || ''}</Field>
				</FieldGroup>

				<FieldGroup title="Activity" expandable>
					<Field title="Next Step">{campaign.next_step}</Field>
					<Field title="Next Step Date">{moment(campaign.next_step_date).format("YYYY/MM/DD")}</Field>

					<Field title="Next Step POC">{campaign.next_step_poc}</Field>
					<Field title="Next Step Owner"></Field>
				</FieldGroup>

				<FieldGroup title="Contact Information" expandable>
					<Field title="Start Date">{moment(campaign.start_date).format("YYYY/MM/DD")}</Field>
					<Field title="End Date">{moment(campaign.end_date).format("YYYY/MM/DD")}</Field>

					{/* <Field title="Sender Email">{campaign.sender_email}</Field>
					<Field title="Email Subject">{campaign.email_header}</Field> */}
				</FieldGroup>

				<FieldGroup title="Results" expandable>
					<Field title="Responses in Campaign">{campaign['num_of_responses']}</Field>
					<Field title="Won Opportunities in Campaign">{campaign['num_of_won_opportunities']}</Field>
					<Field title="Opportunities in Campaign">{campaign['num_of_opportunities']}</Field>
					<Field title="Values Opportunities in Campaign">{campaign['value_of_opportunities']}</Field>
					<Field title="Values Won Opportunities in Campaign">{campaign['value_of_won_opportunities']}</Field>
					<Field title="# Sent">{campaign['num_sent']}</Field>
					{/* <Field title="# of Responses">{campaign['num_of_responses']}</Field> */}
					<Field title="# in Communication">{campaign['num_in_communication']}</Field>
					<Field title="# Contacted">{campaign['num_contacted']}</Field>
					<Field title="# Out of Scope">{campaign['num_out_of_scope']}</Field>
					<Field title="# of Prospects Converted">{campaign['num_of_prospects_converted']}</Field>
				</FieldGroup>

				{ campaign.stepsStats && renderStepStats() }
			</div>

		</PageBase>
	)
};

export default ViewCampaign;
